import { MantineProvider, v8CssVariablesResolver } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import ErrorBoundary from "components/ErrorBoundary";
import ScrollToTop from "components/ScrollToTop";
import { OpenCvProvider } from "opencv-react";
import { StrictMode, Suspense } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router";
import App from "./App";
import theme from "./common/theme";
import { LiveInvalidationProvider } from "./providers/LiveInvalidationProvider";
import WithConnectionHandler from "./providers/WithConnectionsHandler";
import WithPdfJsWorker from "./providers/WithPdfJsWorker";
import WithTanStackQuery from "./providers/WithTanStackQuery";

import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/thumbnail/lib/styles/index.css";
import "dayjs/locale/fi";
import "./common/i18n";
import "./index.css";
import sentryService from "services/SentryService";
import { SentryErrorBoundary } from "services/SentryService/SentryService";
import { SocketProvider } from "src/socket/SocketProvider";

if ("serviceWorker" in navigator) {
  navigator.serviceWorker.ready.then(registration => {
    registration.addEventListener("updatefound", () => {
      const newWorker = registration.installing;
      newWorker?.addEventListener("statechange", () => {
        if (
          newWorker.state === "installed" &&
          navigator.serviceWorker.controller
        ) {
          newWorker.postMessage({ type: "SKIP_WAITING" });
        }
      });
    });
  });
}

sentryService.init();

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <StrictMode>
    <SentryErrorBoundary
      fallback={<div>An error has occur. Our team has been notified.</div>}
    >
      <MantineProvider theme={theme} cssVariablesResolver={v8CssVariablesResolver}>
        <OpenCvProvider>
          <WithPdfJsWorker>
            <Notifications limit={3} position="top-right" zIndex={1000} />
            <WithTanStackQuery>
              <WithConnectionHandler>
                <SocketProvider>
                  <Suspense fallback={null}>
                    <BrowserRouter>
                      <ErrorBoundary>
                        <ScrollToTop />
                        <App />
                      </ErrorBoundary>
                    </BrowserRouter>
                  </Suspense>
                  <LiveInvalidationProvider />
                </SocketProvider>
              </WithConnectionHandler>
              {import.meta.env.VITE_QUERY_DEVTOOLS === "true" && (
                <ReactQueryDevtools
                  buttonPosition="top-left"
                  position="bottom"
                />
              )}
            </WithTanStackQuery>
          </WithPdfJsWorker>
        </OpenCvProvider>
      </MantineProvider>
    </SentryErrorBoundary>
  </StrictMode>
);
